// react and js
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { React, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

// mui
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  AppBar,
  Typography,
  Popper,
  Paper,
  ClickAwayListener,
} from "@mui/material";
import TranslateIcon from "@mui/icons-material/Translate";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

// components

// hooks
import defines from "../hooks/defines";

// i18n
import {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  getLangFromPath,
  buildPathForLang,
  localizeWebsiteUrl,
} from "../i18n";

// layouts

// pages

// theme

// utils

// widgets

// ----------------------------------------------------------------------
// Properties
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function PublicHeader() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Active language is whatever the URL says, not local state.
  const activeLang = getLangFromPath(location.pathname) || DEFAULT_LANGUAGE;
  const currentLang =
    SUPPORTED_LANGUAGES.find((l) => l.code === activeLang) ||
    SUPPORTED_LANGUAGES[0];

  const [langOpen, setLangOpen] = useState(false);
  const [langPinned, setLangPinned] = useState(false);
  const langRef = useRef(null);

  const handleSelectLang = (code) => {
    setLangOpen(false);
    setLangPinned(false);
    if (code === activeLang) return;
    const target = buildPathForLang(code, location.pathname);
    navigate(`${target}${location.search || ""}`);
  };

  return (
    <AppBar
      sx={{
        background: "#F2F2F2",
        boxShadow: "0px 0px 2px rgba(0,0,0,0.5)",
        minHeight: isDesktop
          ? defines.headerHeightDesktop
          : defines.headerHeightMobile,
        maxHeight: isDesktop
          ? defines.headerHeightDesktop
          : defines.headerHeightMobile,
      }}
    >
      {isDesktop && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          <Typography variant="topbar">{t("header.checkout")}</Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          minHeight: isDesktop
            ? defines.headerHeightDesktop
            : defines.headerHeightMobile,
          maxHeight: isDesktop
            ? defines.headerHeightDesktop
            : defines.headerHeightMobile,
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <RouterLink
            to={localizeWebsiteUrl(
              process.env.REACT_APP_URL_FRONTEND_WEBSITE,
              activeLang
            )}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <Box
              component="img"
              src={process.env.REACT_APP_PATH_ICON_SCALEABLE}
              sx={{
                marginTop: isDesktop ? "-1px" : "-2px",
                marginLeft: isDesktop ? "22px" : "16px",
                width: "38px",
                height: "38px",
              }}
            />

            <Typography
              sx={{
                marginLeft: "12px",
                fontSize: "22px",
                fontWeight: 700,
                color: "#2E2E2E",
                letterSpacing: 0.15,
                whiteSpace: "nowrap",
              }}
            >
              {process.env.REACT_APP_NAME_BIZ}
            </Typography>
          </RouterLink>
        </Box>

        <ClickAwayListener
          onClickAway={() => {
            setLangOpen(false);
            setLangPinned(false);
          }}
        >
          <Box
            ref={langRef}
            onMouseEnter={() => {
              if (!langOpen) setLangOpen(true);
            }}
            onMouseLeave={() => {
              if (!langPinned) setLangOpen(false);
            }}
            sx={{
              position: "relative",
              marginRight: isDesktop ? "22px" : "16px",
            }}
          >
            <Box
              onClick={() => {
                if (langPinned) {
                  setLangOpen(false);
                  setLangPinned(false);
                } else {
                  setLangOpen(true);
                  setLangPinned(true);
                }
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                gap: 0.5,
                px: 1,
                py: 0.5,
                color: "#555",
                "&:hover": { color: "#73E555" },
              }}
            >
              <TranslateIcon sx={{ fontSize: 18, color: "inherit" }} />
              <Typography
                variant="contentSmall"
                sx={{ fontSize: "13px", color: "inherit" }}
              >
                {currentLang?.nativeName || "English"}
              </Typography>
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: 16,
                  color: "inherit",
                  transition: "transform 0.25s ease",
                  transform: langOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </Box>
            <Popper
              open={langOpen}
              anchorEl={langRef.current}
              placement="bottom-end"
              sx={{ zIndex: 1300 }}
            >
              <Paper
                sx={{
                  maxHeight: 400,
                  width: 180,
                  borderRadius: "8px",
                  mt: 0.5,
                  py: 0.5,
                  overflowY: "auto",
                  opacity: langOpen ? 1 : 0,
                  transform: langOpen ? "translateY(0)" : "translateY(-4px)",
                  transition: "opacity 0.2s ease, transform 0.2s ease",
                }}
              >
                {SUPPORTED_LANGUAGES.map((l) => (
                  <Box
                    key={l.code}
                    onClick={() => handleSelectLang(l.code)}
                    sx={{
                      fontSize: "14px",
                      py: 0.75,
                      px: 2,
                      cursor: "pointer",
                      color: l.code === activeLang ? "#73E555" : "#333",
                      "&:hover": { color: "#73E555" },
                    }}
                  >
                    {l.nativeName}
                  </Box>
                ))}
              </Paper>
            </Popper>
          </Box>
        </ClickAwayListener>
      </Box>
    </AppBar>
  );
}
